import React, { useState, useMemo } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { feedbackStrings, commonStrings } from '@/constants/residents/marketplaceStrings';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ListingBuyer } from '@/types/residents/marketplaceListing';
import { BaseModal, UserAvatar, RadioButton } from './shared';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const SearchIcon = SVG_COMPONENTS.MAGNIFYING_GLASS;
const SuccessIcon = RESIDENTS_SVG_COMPONENTS.CELEBRATION;

interface SoldFeedbackModalProps {
  visible: boolean;
  buyers: ListingBuyer[];
  onClose: () => void;
  onSubmit: (buyerId: string | null) => void;
}

/**
 * Modal shown after marking an item as sold when buyers have messaged about it.
 * Allows seller to select who they sold the item to for feedback purposes.
 */
export const SoldFeedbackModal: React.FC<SoldFeedbackModalProps> = ({
  visible,
  buyers,
  onClose,
  onSubmit,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);

  // Filter buyers by search query
  const filteredBuyers = useMemo(() => {
    if (!searchQuery.trim()) {
      return buyers;
    }
    const query = searchQuery.toLowerCase();
    return buyers.filter((buyer) =>
      buyer.name.toLowerCase().includes(query)
    );
  }, [buyers, searchQuery]);

  const handleSubmit = () => {
    onSubmit(selectedBuyerId);
  };

  const handleClose = () => {
    // Reset state when closing
    setSearchQuery('');
    setSelectedBuyerId(null);
    onClose();
  };

  const footer = (
    <View style={styles.footerContainer}>
      <TouchableOpacity
        style={[styles.submitButton, { backgroundColor: MARKETPLACE_COLORS.buttonPrimary }]}
        onPress={handleSubmit}
        activeOpacity={0.8}
      >
        <Text style={[styles.submitButtonText, { color: MARKETPLACE_COLORS.buttonPrimaryText }]}>{commonStrings.submit}</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <BaseModal
      visible={visible}
      onClose={handleClose}
      showHeader={true}
      footer={footer}
      size="large"
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Celebration Icon */}
        <View style={styles.iconContainer}>
          <SuccessIcon width={100} height={100} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: MARKETPLACE_COLORS.textPrimary }]}>
          {feedbackStrings.congratsSold}
        </Text>

        {/* Search Input */}
        <View style={[styles.searchContainer, { backgroundColor: MARKETPLACE_COLORS.white5Percent }]}>
          <SearchIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
          <TextInput
            style={[styles.searchInput, { color: MARKETPLACE_COLORS.textPrimary }]}
            placeholder={feedbackStrings.search}
            placeholderTextColor={MARKETPLACE_COLORS.textSecondary}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        {/* Buyers List */}
        <View style={styles.buyersList}>
          {filteredBuyers.map((buyer) => (
            <TouchableOpacity
              key={buyer.id}
              style={[styles.buyerCard, { backgroundColor: MARKETPLACE_COLORS.white5Percent }]}
              onPress={() => setSelectedBuyerId(buyer.id)}
              activeOpacity={0.8}
            >
              {/* Avatar */}
              <UserAvatar
                initials={buyer.initials}
                avatarUrl={buyer.avatarUrl}
                size="small"
              />

              {/* Name */}
              <Text style={[styles.buyerName, { color: MARKETPLACE_COLORS.textPrimary }]}>{buyer.name}</Text>

              {/* Radio Button */}
              <RadioButton isSelected={selectedBuyerId === buyer.id} />
            </TouchableOpacity>
          ))}

          {filteredBuyers.length === 0 && (
            <Text style={[styles.noResultsText, { color: MARKETPLACE_COLORS.textSecondary }]}>{feedbackStrings.noBuyersFound}</Text>
          )}
        </View>
      </ScrollView>
    </BaseModal>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 24,
    gap: 24,
    alignItems: 'center',
  },
  iconContainer: {
    width: 100,
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 36,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 14,
    height: 40,
    gap: 8,
    width: '100%',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    lineHeight: 21,
  },
  buyersList: {
    width: '100%',
    gap: 12,
  },
  buyerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 16,
  },
  buyerName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  noResultsText: {
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },
  footerContainer: {
    width: '100%',
  },
  submitButton: {
    width: '100%',
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
});
